import { createContext, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * The slot a page uses to speak to the shell (docs/platform-standards.md
 * §13.2). A page never draws a header; it contributes three things:
 *
 *   useShellSlot({ title, crumbTail })   a title override and a last crumb
 *                                        (or a list of crumbs after the collection)
 *   <ShellActions>…</ShellActions>       the page's own actions, drawn in the
 *                                        header's actions cluster
 *
 * Strings travel through the layout's state, so a page that re-renders with the
 * same words costs the shell nothing. Actions are PORTALED into a node the
 * header owns, not copied into state: they stay in the page's own React tree,
 * so their state and context keep working and there is no effect that has to
 * chase a new element every render.
 *
 * Outside a shell (a page rendered bare in a test) the hooks do nothing and
 * <ShellActions> renders nothing, so a page may use the slot unconditionally.
 */
export const ShellSlotContext = createContext(null);

/**
 * `setSlot` must be a stable setter (a useState setter), so using it never re-renders the page.
 * `off`: a component that is not the page (the item form inside the list's detail pane, KPR-1644)
 * says nothing, so it neither names the crumb nor clears the page's.
 */
export function useShellSlot({ title = null, crumbTail = null, off = false } = {}) {
	const slot = useContext(ShellSlotContext);
	const setSlot = off ? null : slot?.setSlot;
	// A tail may be a list of crumbs (`[{ label, to? }]`, an editor under a card,
	// KPR-1287). It is compared by what it says, not by identity, so a page that
	// builds the list every render does not re-render the shell every render.
	const tailKey = Array.isArray(crumbTail) ? JSON.stringify(crumbTail) : crumbTail;
	const latestTail = useRef(crumbTail);
	latestTail.current = crumbTail;
	useEffect(() => {
		if (!setSlot) return undefined;
		setSlot({ title, crumbTail: latestTail.current });
		return () => setSlot({ title: null, crumbTail: null });
	}, [setSlot, title, tailKey]);
}

export function ShellActions({ children }) {
	const slot = useContext(ShellSlotContext);
	const node = slot?.actionsNode;
	if (!node || !children) return null;
	return createPortal(children, node);
}

/**
 * The page's own entries for the header's one ⋯ (the scope draws it; a page
 * never draws a second). `actions` are the flat objects buildActionMenu takes.
 * The shell keeps a copy whose handlers call the page's LATEST ones, so the
 * page may rebuild the array every render and the header only updates when the
 * entries' keys, labels or disabled flags change.
 */
export function useShellMenu(actions) {
	const slot = useContext(ShellSlotContext);
	const setMenuActions = slot?.setMenuActions;
	const latest = useRef([]);
	const list = Array.isArray(actions) ? actions.filter(Boolean) : [];
	latest.current = list;
	const signature = list.map(a => `${a.key}|${a.group || ''}|${typeof a.label === 'string' ? a.label : ''}|${a.disabled ? 1 : 0}`).join(';');
	useEffect(() => {
		if (!setMenuActions) return undefined;
		setMenuActions(latest.current.map(a => ({
			...a,
			onClick: a.onClick ? (...args) => latest.current.find(x => x.key === a.key)?.onClick?.(...args) : undefined,
		})));
		return () => setMenuActions([]);
	}, [setMenuActions, signature]);
}
